import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { proxyUrl } from "./utils";
import { usePermissionSeedStore } from "../store/usePermissionSeedStore";

const axiosInstance = axios.create({
  baseURL: proxyUrl(), // Replace with your API base URL
  timeout: 10000,
  withCredentials: true,
});

const AUTH_BYPASS_PATHS = [
  "/api/tenant/login",
  "/api/admin/login",
  "/api/tenant/me",
  "/api/tenant/logout",
  "/api/admin/logout",
  "/api/tenant/forgot-password",
  "/api/tenant/reset-password",
  "/api/tenant/reset-password-with-token",
  "/api/tenant/validate-reset-token",
  "/api/admin/signup",
];
const REFRESH_PATH = "/api/tenant/refresh-token";

function matchesPath(config: { url?: string } | undefined, paths: string[]): boolean {
  const path = (config?.url || "").split("?")[0];
  return paths.some((p) => path === p || path.endsWith(p));
}

function isAuthBypassRequest(config?: { url?: string }): boolean {
  return matchesPath(config, AUTH_BYPASS_PATHS);
}

function isRefreshRequest(config?: { url?: string }): boolean {
  return matchesPath(config, [REFRESH_PATH]);
}


let refreshPromise: Promise<void> | null = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post(REFRESH_PATH)
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export const setupInterceptors = (
  addMessage: (
    message: string,
    type: "success" | "error" | "info" | "warning"
  ) => void,
  logoutCallback?: () => void
) => {

  let isLoggingOut = false;
  const forceLogout = () => {
    if (isLoggingOut) return;
    isLoggingOut = true;
    addMessage("Session expired. Please log in again.", "warning");
    if (logoutCallback) {
      logoutCallback();
    } else {
      localStorage.clear();
      usePermissionSeedStore.getState().clearSeed();
      window.location.href = "/login";
    }
  };


  const interceptor = axiosInstance.interceptors.response.use(
    (response) => {
      if (
        response.config.url?.endsWith("/api/tenant/login") ||
        response.config.url?.endsWith("/api/admin/login")
      ) {
        addMessage("Login successful!", "success");
      }
      return response;
    },
    async (error: AxiosError) => {
      if (error.response) {
        const status = error.response?.status;

        const config = error.config as
          | (InternalAxiosRequestConfig & {
            _retry?: boolean;
          })
          | undefined;

        if (!error.response) {
          console.error("Network error or no response.");
          return Promise.reject(error);
        }

        if (status === 401) {
          if (isAuthBypassRequest(config)) {
            return Promise.reject(error);
          }

          if (isRefreshRequest(config)) {
            forceLogout();
            return Promise.reject(error);
          }

          if (config?._retry) {
            forceLogout();
            return Promise.reject(error);
          }

          if (!config) {
            forceLogout();
            return Promise.reject(error);
          }

          config._retry = true;

          try {
            await refreshAccessToken();
            return axiosInstance(config);
          } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
          }
        }

        switch (status) {
          case 403:
            console.error("Forbidden access.");
            addMessage("You do not have permission to perform this action.", "error");
            break;
          case 404:
            console.error("Resource not found.");
            break;
          case 500:
            console.error("Server error.");
            break;
          default:
            console.error(
              `Error: ${status} - ${(error.response.data as any)?.message || "Unknown error"}`
            );
        }
      }
      return Promise.reject(error);
    }
  );
  return interceptor;
};

export default axiosInstance;
